import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import Button from '../../../components/ui/Button';
import RegistrationForm from './components/RegistrationForm';
import ProgressIndicator from './components/ProgressIndicator';
import SocialRegistration from './components/SocialRegistration';

const NameEmailRegistration = () => {
    const navigate = useNavigate();
    const { t } = useTranslation('common');
    const [formData, setFormData] = useState({
        firstName: '',
        lastName: '',
        email: '',
        password: '',
        confirmPassword: ''
    });
    const [registrationData, setRegistrationData] = useState(null);

    useEffect(() => {
        // Get data from previous step
        const storedData = localStorage.getItem('registrationData');
        if (!storedData) {
            // If no previous data, redirect back to country selection
            navigate('/country-language-selection');
            return;
        }
        setRegistrationData(JSON.parse(storedData));
    }, [navigate]);

    const handleFormSubmit = (data) => {
        // Combine with previous registration data
        const completeData = {
            ...registrationData,
            ...data,
            step: 'name-email-completed'
        };

        // Store updated data
        localStorage.setItem('registrationData', JSON.stringify(completeData));

        // Navigate to next step
        navigate('/auth/birthday-gender-selection');
    };

    const handleBack = () => {
        navigate('/country-language-selection');
    };

    const handleSocialRegistration = (provider, userData) => {
        // Handle social registration
        const completeData = {
            ...registrationData,
            socialProvider: provider,
            ...userData,
            step: 'social-completed'
        };

        localStorage.setItem('registrationData', JSON.stringify(completeData));
        navigate('/auth/birthday-gender-selection');
    };

    if (!registrationData) {
        return <div>Loading...</div>;
    }

    return (
        <div className="min-h-screen bg-gradient-to-br from-primary-50 to-secondary-50 flex items-center justify-center p-4">
            <div className="w-full max-w-md">
                {/* Logo */}
                <div className="text-center mb-8">
                    <div className="flex justify-center mb-4">
                        <img
                            src="/assets/images/Giftsy-Logo-Final-1751671984839.png"
                            alt="Giftsy Modern"
                            className="h-16 w-auto"
                        />
                    </div>
                    <h1 className="text-2xl font-heading font-bold text-accent-800 mb-2">
                        Create Your Account
                    </h1>
                    <p className="text-accent-600 text-base">
                        Enter your personal details to get started
                    </p>
                </div>

                {/* Progress Indicator */}
                <ProgressIndicator currentStep={2} totalSteps={3} />

                {/* Registration Card */}
                <div className="bg-white rounded-xl shadow-soft p-6 mb-6">
                    {/* Registration Form */}
                    <RegistrationForm
                        initialData={formData}
                        onSubmit={handleFormSubmit}
                        countryCode={registrationData.country?.code}
                    />

                    {/* Divider */}
                    <div className="relative my-6">
                        <div className="absolute inset-0 flex items-center">
                            <div className="w-full border-t border-accent-200"></div>
                        </div>
                        <div className="relative flex justify-center text-sm">
                            <span className="px-2 bg-white text-accent-500">Or register with</span>
                        </div>
                    </div>

                    {/* Social Registration Options */}
                    <SocialRegistration onSocialRegister={handleSocialRegistration} />

                    {/* Back Button */}
                    <button
                        onClick={handleBack}
                        className="w-full mt-4 text-accent-600 hover:text-primary-600 text-sm font-medium transition-colors"
                    >
                        ← Back to Country Selection
                    </button>
                </div>

                {/* Terms Note */}
                <div className="text-center text-xs text-accent-500 max-w-sm mx-auto">
                    By creating an account, you agree to our{' '}
                    <a href="/terms" className="text-primary-600 hover:underline">
                        Terms of Service
                    </a>{' '}
                    and{' '}
                    <a href="/privacy" className="text-primary-600 hover:underline">
                        Privacy Policy
                    </a>
                </div>
            </div>
        </div>
    );
};

export default NameEmailRegistration; 